import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { lastValueFrom, Observable, take } from 'rxjs';
import { HIGHLEVEL_GAPI_PATHS } from '@common/domain/highlevel-gapi-paths';
import { firebase, gapiSettings, useFirebaseEmulators } from 'src/environments';
import {
	HighLevelContact,
	HighLevelInactiveParentsCreateResult,
	LinkMasHighLevelIdsResult,
	Schedule,
	SyncActiveParentContactsResult,
} from '../models';
import {
	AdvancedDebugging,
	EmailMessage,
	httpErrorStatus,
	isExpectedClientHttpError,
	isGoogleGroupMemberAlreadyRemoved,
	SendEmail,
} from '../utilities';
import { GoogleIdentityService } from './identity.service';

export interface ViolationsJobResult {
	closeOnlyCount: number;
	dryRun: boolean;
	endCutoff: string;
	matched: number;
	reserveProgramCount: number;
	reserveProgramNames: string[];
	reserveScheduleCount: number;
	violationCount: number;
	violations?: unknown[];
}

export interface ScheduleLegacyMetadataBackfillResult {
	ok: boolean;
	dryRun: boolean;
	scanned: number;
	matched: number;
	updated: number;
	skipped: number;
	errors: number;
	lessonMatches: number;
	eventMatches: number;
	logs: unknown[];
	source?: string;
}

@Injectable({
	providedIn: 'root',
})
export class Firebase_Functions_Service {
	private static readonly opGetCalendarEvents = 'app:Firebase_Functions_Service.getCalendarEventsFromGoogle';

	private readonly advancedDebugging = new AdvancedDebugging();
	private emulatorFunctionsEndpoint = `http://localhost:5001/${firebase.Cfg.projectId}/us-central1/gapi`;
	private emulatorSandboxEndpoint = `http://localhost:5001/${firebase.Cfg.projectId}/us-central1/sandbox`;

	private createCorrelationId(): string {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID();
		}

		return `cid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
	}

	private isLocalFirebaseHost(): boolean {
		const host = window.location.hostname;
		return host === 'localhost' || host === '127.0.0.1';
	}

	private getFunctionsEndpoint(): string {
		return this.isLocalFirebaseHost() && useFirebaseEmulators ? this.emulatorFunctionsEndpoint : gapiSettings.Cfg.gapiEndpoint;
	}

	private getSandboxEndpoint(): string {
		if (this.isLocalFirebaseHost() && useFirebaseEmulators) {
			return this.emulatorSandboxEndpoint;
		}

		return gapiSettings.Cfg.sandboxEndpoint;
	}

	private async invokeHttp<T>(operation: string, request: () => Promise<T>, context?: Record<string, unknown>): Promise<T> {
		try {
			return await request();
		} catch (error) {
			if (!isExpectedClientHttpError(error)) {
				this.advancedDebugging.captureError(operation, error, {
					tags: { operation },
					...context,
				});
			}
			throw error;
		}
	}

	constructor(
		private http: HttpClient,
		private identityService: GoogleIdentityService,
		public firestore: Firestore,
	) {}

	/* ------------------------------------------------------------
		AUTH HEADER HELPER
	------------------------------------------------------------- */

	private async buildAuthHeaders(): Promise<HttpHeaders | null> {
		const forceRefresh = this.isLocalFirebaseHost() && useFirebaseEmulators;
		const token = await this.identityService.getIdTokenForHttp(forceRefresh);
		if (!token) return null;

		return new HttpHeaders({
			Authorization: `Bearer ${token}`,
			'x-correlation-id': this.createCorrelationId(),
		});
	}

	/*************************************************************************
	 * GAPI API
	 *************************************************************************/

	// Add member to group (POST)
	async getAddMember(group: string, email: string): Promise<any> {
		const headers = await this.buildAuthHeaders();
		if (!headers) return { status: 400 };

		return this.invokeHttp('app:Firebase_Functions_Service.getAddMember', () =>
			lastValueFrom(this.http.post(`${this.getFunctionsEndpoint()}/addMember`, { group, email }, { headers })),
		);
	}

	// Calendar Events (GET)
	async getCalendarEventsFromGoogle(filter: string): Promise<any[]> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		const start = new Date().toISOString();
		const params = new HttpParams().set('start', start).set('filter', filter || '');

		return this.invokeHttp(
			Firebase_Functions_Service.opGetCalendarEvents,
			async () => {
				const response = await lastValueFrom(
					this.http.get<any>(`${this.getFunctionsEndpoint()}/events`, {
						headers,
						params,
					}),
				);
				const events = response?.items || [];
				if (this.isLocalFirebaseHost()) {
					this.advancedDebugging.log(Firebase_Functions_Service.opGetCalendarEvents, {
						eventCount: events.length,
						filter,
					});
				}
				return events;
			},
			{ filter },
		);
	}

	// Initialize Google OAuth (GET). Use promptConsent when a new offline refresh token is required.
	async getGoogleLogin(opts?: { promptConsent?: boolean }) {
		const headers = await this.buildAuthHeaders();
		if (!headers) return;

		const loginUrl =
			opts?.promptConsent === true
				? `${this.getFunctionsEndpoint()}/googleLogin?prompt=consent`
				: `${this.getFunctionsEndpoint()}/googleLogin`;

		const result = await this.invokeHttp('app:Firebase_Functions_Service.getGoogleLogin', () =>
			lastValueFrom(this.http.get<{ authUrl: string }>(loginUrl, { headers })),
		);

		window.open(result.authUrl, '_blank');
		return true;
	}

	/** Refresh stored Google access token; returns invalid_grant when re-login is required. */
	async refreshGoogleGapiToken(): Promise<{
		ok: boolean;
		expiry_date?: number;
		reason?: 'missing_refresh_token' | 'invalid_grant' | 'refresh_failed' | 'no_account' | 'auth_unavailable';
		message?: string;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) {
			return { ok: false, reason: 'auth_unavailable' };
		}

		type RefreshReason = 'missing_refresh_token' | 'invalid_grant' | 'refresh_failed' | 'no_account';

		try {
			const response = await lastValueFrom(
				this.http.post<{ ok: boolean; expiry_date?: number; reason?: RefreshReason; message?: string }>(
					`${this.getFunctionsEndpoint()}/token/refresh`,
					{},
					{ headers },
				),
			);
			return response;
		} catch (error: unknown) {
			const httpError = error as { status?: number; error?: { reason?: RefreshReason; message?: string } };
			const body = httpError?.error;
			if (httpError?.status === 401 && body?.reason) {
				return { ok: false, reason: body.reason, message: body.message };
			}
			if (httpError?.status === 404) {
				return { ok: false, reason: 'no_account' };
			}
			this.advancedDebugging.captureError('app:Firebase_Functions_Service.refreshGoogleGapiToken', error, {
				tags: { operation: 'app:Firebase_Functions_Service.refreshGoogleGapiToken' },
			});
			return { ok: false, reason: 'refresh_failed' };
		}
	}

	/** Server proxy: US public holidays for a given year from Nager.Date. */
	async getUSHolidaysForYear(year: number): Promise<{
		ok: boolean;
		year: number;
		countryCode: 'US';
		holidays: { date: string; holiday: string }[];
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.getUSHolidaysForYear',
			() =>
				lastValueFrom(
					this.http.get<{
						ok: boolean;
						year: number;
						countryCode: 'US';
						holidays: { date: string; holiday: string }[];
					}>(`${this.getFunctionsEndpoint()}/holidays/us/${year}`, { headers }),
				),
			{ year },
		);
	}

	// Get Google Group info (GET)
	async getGroup(group: string): Promise<any> {
		const headers = await this.buildAuthHeaders();
		if (!headers) return { status: 400 };

		const params = new HttpParams().set('group', group);

		return this.invokeHttp('app:Firebase_Functions_Service.getGroup', () =>
			lastValueFrom(
				this.http.get(`${this.getFunctionsEndpoint()}/group`, {
					headers,
					params,
				}),
			),
		);
	}

	// Get Google Group Members (GET)
	async getMembers(group: string): Promise<any> {
		const headers = await this.buildAuthHeaders();
		if (!headers) return { status: 400 };

		const params = new HttpParams().set('group', group);

		return this.invokeHttp('app:Firebase_Functions_Service.getMembers', () =>
			lastValueFrom(
				this.http.get(`${this.getFunctionsEndpoint()}/members`, {
					headers,
					params,
				}),
			),
		);
	}

	// Remove member (POST)
	async removeGroupMember(group: string, email: string): Promise<any> {
		const headers = await this.buildAuthHeaders();
		if (!headers) return { status: 400 };

		return this.invokeHttp('app:Firebase_Functions_Service.removeGroupMember', async () => {
			try {
				return await lastValueFrom(
					this.http.post(`${this.getFunctionsEndpoint()}/removeMember`, { group, email }, { headers }),
				);
			} catch (error) {
				if (isGoogleGroupMemberAlreadyRemoved(error)) {
					return { ok: true, alreadyRemoved: true };
				}
				throw error;
			}
		});
	}

	// Create shared contact (POST)
	async createSharedContact(contact: { name: string; email: string }) {
		const headers = await this.buildAuthHeaders();
		if (!headers) return { status: 400 };

		return this.invokeHttp(
			'app:Firebase_Functions_Service.createSharedContact',
			() => lastValueFrom(this.http.post(`${this.getFunctionsEndpoint()}/createSharedContact`, contact, { headers })),
		);
	}

	// Get directory (GET)
	async getDirectory(): Promise<any> {
		const headers = await this.buildAuthHeaders();
		if (!headers) return { status: 400 };

		return this.invokeHttp('app:Firebase_Functions_Service.getDirectory', () =>
			lastValueFrom(this.http.get(`${this.getFunctionsEndpoint()}/directory`, { headers })),
		);
	}

	// Remove shared contact (POST)
	async removeSharedContact(id: string): Promise<any> {
		const headers = await this.buildAuthHeaders();
		if (!headers) return { status: 400 };

		return this.invokeHttp('app:Firebase_Functions_Service.removeSharedContact', () =>
			lastValueFrom(this.http.post(`${this.getFunctionsEndpoint()}/removeSharedContact`, { id }, { headers })),
		);
	}

	// Atomic attendance upsert/remove/wait-list (POST)
	async enrollInScheduleAtomic(payload: {
		scheduleId: string;
		memberId: string;
		memberName?: string;
		lessonCategory?: string;
		notifications?: any;
		attended?: boolean;
		reserved?: boolean;
		mode?:
			| 'upsert'
			| 'remove'
			| 'waitListAdd'
			| 'waitListRemove'
			| 'dedupeMember'
			| 'syncMemberName'
			| 'syncNotificationName'
			| 'syncNotificationPrefs';
		source?: string;
		waitListEntry?: any;
	}): Promise<{
		ok: boolean;
		changed: boolean;
		attendance: any[];
		waitList?: any[];
		scheduleId: string;
		mode:
			| 'upsert'
			| 'remove'
			| 'waitListAdd'
			| 'waitListRemove'
			| 'dedupeMember'
			| 'syncMemberName'
			| 'syncNotificationName'
			| 'syncNotificationPrefs';
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		const mode = payload.mode ?? 'upsert';
		const operation = `app:Firebase_Functions_Service.enrollInScheduleAtomic.${mode}`;
		return this.invokeHttp(
			operation,
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						changed: boolean;
						attendance: any[];
						waitList?: any[];
						scheduleId: string;
						mode:
							| 'upsert'
							| 'remove'
							| 'waitListAdd'
							| 'waitListRemove'
							| 'dedupeMember'
							| 'syncMemberName'
							| 'syncNotificationName'
							| 'syncNotificationPrefs';
					}>(`${this.getFunctionsEndpoint()}/attendance/enrollAtomic`, payload, { headers }),
				),
			{ scheduleId: payload.scheduleId, mode },
		);
	}

	async mutateCycleEventAtomic(payload: {
		eventId?: string;
		eventPatch?: Record<string, unknown>;
		mode:
			| 'attendanceToggle'
			| 'guestAdd'
			| 'guestRemove'
			| 'adminUpsert'
			| 'adminCreate'
			| 'adminDelete'
			| 'acknowledgementAdd';
		accountId?: string;
		memberId?: string;
		attendanceEntry?: {
			attended: boolean;
			id: string;
			name: string;
			notifications?: unknown;
			reserved: boolean;
		};
		guestEntry?: { sponsor: string; id: string; name: string; reserved: boolean };
		sponsorId?: string;
		guestIndex?: number;
		source?: string;
	}): Promise<{
		ok: boolean;
		changed: boolean;
		eventId: string;
		mode:
			| 'attendanceToggle'
			| 'guestAdd'
			| 'guestRemove'
			| 'adminUpsert'
			| 'adminCreate'
			| 'adminDelete'
			| 'acknowledgementAdd';
		acknowledgements?: unknown[];
		attendance: unknown[];
		guest: unknown[];
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		const operation = `app:Firebase_Functions_Service.mutateCycleEventAtomic.${payload.mode}`;
		return this.invokeHttp(
			operation,
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						changed: boolean;
						eventId: string;
						mode:
							| 'attendanceToggle'
							| 'guestAdd'
							| 'guestRemove'
							| 'adminUpsert'
							| 'adminCreate'
							| 'adminDelete'
							| 'acknowledgementAdd';
						acknowledgements?: unknown[];
						attendance: unknown[];
						guest: unknown[];
					}>(`${this.getFunctionsEndpoint()}/events/mutateAtomic`, payload, { headers }),
				),
			{ mode: payload.mode, eventId: payload.eventId },
		);
	}

	async mutateScheduleEventAtomic(payload: {
		scheduleId: string;
		mode: 'attendanceToggle' | 'guestAdd' | 'guestRemove' | 'acknowledgementAdd';
		accountId?: string;
		memberId?: string;
		attendanceEntry?: {
			attended: boolean;
			id: string;
			name: string;
			notifications?: unknown;
			reserved: boolean;
		};
		guestEntry?: { sponsor: string; id: string; name: string; reserved: boolean };
		sponsorId?: string;
		guestIndex?: number;
		source?: string;
	}): Promise<{
		ok: boolean;
		changed: boolean;
		scheduleId: string;
		mode: 'attendanceToggle' | 'guestAdd' | 'guestRemove' | 'acknowledgementAdd';
		acknowledgements?: unknown[];
		attendance: unknown[];
		guest: unknown[];
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		const operation = `app:Firebase_Functions_Service.mutateScheduleEventAtomic.${payload.mode}`;
		return this.invokeHttp(
			operation,
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						changed: boolean;
						scheduleId: string;
						mode: 'attendanceToggle' | 'guestAdd' | 'guestRemove' | 'acknowledgementAdd';
						acknowledgements?: unknown[];
						attendance: unknown[];
						guest: unknown[];
					}>(`${this.getFunctionsEndpoint()}/schedules/mutateEventAtomic`, payload, { headers }),
				),
			{ mode: payload.mode, scheduleId: payload.scheduleId },
		);
	}

	/** Dashboard parents: toggle a staff greeting for a parent on a class schedule. */
	async mutateScheduleGreetingAtomic(payload: {
		scheduleId: string;
		parentAccountId: string;
		mode: 'add' | 'remove';
		source?: string;
	}): Promise<{
		ok: boolean;
		changed: boolean;
		scheduleId: string;
		mode: 'add' | 'remove';
		greetings: Array<{ parentAccountId: string; userId: string; greetedAt: string }>;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		const operation = `app:Firebase_Functions_Service.mutateScheduleGreetingAtomic.${payload.mode}`;
		return this.invokeHttp(
			operation,
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						changed: boolean;
						scheduleId: string;
						mode: 'add' | 'remove';
						greetings: Array<{ parentAccountId: string; userId: string; greetedAt: string }>;
					}>(`${this.getFunctionsEndpoint()}/schedules/mutateGreetingAtomic`, payload, { headers }),
				),
			{ mode: payload.mode, parentAccountId: payload.parentAccountId, scheduleId: payload.scheduleId },
		);
	}

	/** Server: optional cancellation email to reserved members + delete `mas-schedules` doc (single batch). */
	async deleteScheduleWithCancellation(payload: { scheduleId: string; source?: string }): Promise<{
		emailedCount: number;
		ok: boolean;
		scheduleId: string;
		source: string;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.deleteScheduleWithCancellation',
			() =>
				lastValueFrom(
					this.http.post<{
						emailedCount: number;
						ok: boolean;
						scheduleId: string;
						source: string;
					}>(`${this.getFunctionsEndpoint()}/schedules/deleteWithCancellation`, payload, { headers }),
				),
			{ scheduleId: payload.scheduleId },
		);
	}

	/**
	 * Server: remove future program schedules not present in the current Google Calendar id set
	 * when they have no attendance (Google sync cleanup; replaces client Firestore deletes).
	 */
	async pruneGoogleSyncStaleSchedules(payload: {
		afterStartDateTime: string;
		program: string;
		testCategory?: string;
		calendarScheduleIds: string[];
		source?: string;
	}): Promise<{
		ok: boolean;
		logs: { styleColor: string; msg: string }[];
		removedCount: number;
		source: string;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.pruneGoogleSyncStaleSchedules',
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						logs: { styleColor: string; msg: string }[];
						removedCount: number;
						source: string;
					}>(`${this.getFunctionsEndpoint()}/schedules/pruneGoogleSyncStale`, payload, { headers }),
				),
			{ program: payload.program },
		);
	}

	/** Server: batch merge-write Google calendar schedules to `mas-schedules` (max 450 per request). */
	async upsertGoogleSchedulesBatch(payload: {
		schedules: unknown[];
		source?: string;
	}): Promise<{
		ok: boolean;
		insertedCount: number;
		logs: { styleColor: string; msg: string }[];
		source: string;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.upsertGoogleSchedulesBatch',
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						insertedCount: number;
						logs: { styleColor: string; msg: string }[];
						source: string;
					}>(`${this.getFunctionsEndpoint()}/schedules/upsertGoogleBatch`, payload, { headers }),
				),
			{ scheduleCount: payload.schedules?.length ?? 0 },
		);
	}

	/** Server: create or update a class schedule (metadata only; cycle resolved server-side). */
	async upsertClassSchedule(payload: {
		id?: string;
		summary: string;
		programId: string;
		start: { dateTime: string; timeZone: string };
		end: { dateTime: string; timeZone: string };
		source?: string;
	}): Promise<{
		ok: boolean;
		schedule?: Schedule;
		scheduleId?: string;
		source?: string;
		code?: string;
		error?: string;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.upsertClassSchedule',
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						schedule?: Schedule;
						scheduleId?: string;
						source?: string;
						code?: string;
						error?: string;
					}>(`${this.getFunctionsEndpoint()}/schedules/upsertClass`, payload, { headers }),
				),
			{ scheduleId: payload.id ?? 'new', programId: payload.programId },
		);
	}

	/** Server: create or update a test schedule (`eventType: test`; summary must match program testCategory). */
	async upsertTestSchedule(payload: {
		id?: string;
		summary: string;
		programId: string;
		start: { dateTime: string; timeZone: string };
		end: { dateTime: string; timeZone: string };
		source?: string;
	}): Promise<{
		ok: boolean;
		schedule?: Schedule;
		scheduleId?: string;
		source?: string;
		code?: string;
		error?: string;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.upsertTestSchedule',
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						schedule?: Schedule;
						scheduleId?: string;
						source?: string;
						code?: string;
						error?: string;
					}>(`${this.getFunctionsEndpoint()}/schedules/upsertTest`, payload, { headers }),
				),
			{ scheduleId: payload.id ?? 'new', programId: payload.programId },
		);
	}

	/** Server: create or update an event schedule (`eventType: closed`; not program-specific). */
	async upsertEventSchedule(payload: {
		id?: string;
		summary: string;
		start: { dateTime: string; timeZone: string };
		end: { dateTime: string; timeZone: string };
		description?: string;
		image?: string;
		url?: string;
		optionReserve?: boolean;
		reserveLimit?: number;
		guestLimit?: number;
		buttonText?: string;
		source?: string;
	}): Promise<{
		ok: boolean;
		schedule?: Schedule;
		scheduleId?: string;
		source?: string;
		code?: string;
		error?: string;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.upsertEventSchedule',
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						schedule?: Schedule;
						scheduleId?: string;
						source?: string;
						code?: string;
						error?: string;
					}>(`${this.getFunctionsEndpoint()}/schedules/upsertEvent`, payload, { headers }),
				),
			{ scheduleId: payload.id ?? 'new' },
		);
	}

	/** Server: create or update a lesson schedule (`eventType: lesson`; Legacy program only). */
	async upsertLessonSchedule(payload: {
		id?: string;
		summary: string;
		programId: string;
		start: { dateTime: string; timeZone: string };
		end: { dateTime: string; timeZone: string };
		description?: string;
		image?: string;
		url?: string;
		optionReserve?: boolean;
		reserveLimit?: number;
		category?: string;
		instructorId?: string;
		instructorName?: string;
		source?: string;
	}): Promise<{
		ok: boolean;
		schedule?: Schedule;
		scheduleId?: string;
		source?: string;
		code?: string;
		error?: string;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.upsertLessonSchedule',
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						schedule?: Schedule;
						scheduleId?: string;
						source?: string;
						code?: string;
						error?: string;
					}>(`${this.getFunctionsEndpoint()}/schedules/upsertLesson`, payload, { headers }),
				),
			{ scheduleId: payload.id ?? 'new', programId: payload.programId },
		);
	}

	/** Server: one-way import from `mas-lessons` → `mas-schedules` (does not modify legacy collection). */
	async importMasLessonsFromLegacy(payload: {
		dryRun?: boolean;
		onlyFuture?: boolean;
		limit?: number;
		source?: string;
	}): Promise<{
		ok: boolean;
		dryRun: boolean;
		scanned: number;
		created: number;
		updated: number;
		skipped: number;
		errors: number;
		legacyProgramId: string;
		logs: unknown[];
		source?: string;
	}> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.importMasLessonsFromLegacy',
			() =>
				lastValueFrom(
					this.http.post<{
						ok: boolean;
						dryRun: boolean;
						scanned: number;
						created: number;
						updated: number;
						skipped: number;
						errors: number;
						legacyProgramId: string;
						logs: unknown[];
						source?: string;
					}>(`${this.getFunctionsEndpoint()}/schedules/importMasLessons`, payload, { headers }),
				),
			{ dryRun: payload.dryRun ?? true, onlyFuture: payload.onlyFuture ?? true },
		);
	}

	/** Server: backfill legacy lesson/event metadata onto existing `mas-schedules` docs. */
	async backfillScheduleLegacyMetadata(payload: {
		dryRun?: boolean;
		limit?: number;
		overwrite?: boolean;
		source?: string;
	}): Promise<ScheduleLegacyMetadataBackfillResult> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');
		return this.invokeHttp(
			'app:Firebase_Functions_Service.backfillScheduleLegacyMetadata',
			() =>
				lastValueFrom(
					this.http.post<ScheduleLegacyMetadataBackfillResult>(
						`${this.getFunctionsEndpoint()}/schedules/backfillLegacyMetadata`,
						payload,
						{ headers },
					),
				),
			{ dryRun: payload.dryRun ?? true, overwrite: payload.overwrite ?? false },
		);
	}

	/** True when the user must re-authenticate (missing token or HTTP 401). */
	isAuthUnavailableError(error: unknown): boolean {
		if (error instanceof Error && error.message === 'Invalid token') {
			return true;
		}
		return httpErrorStatus(error) === 401;
	}

	authUnavailableUserMessage(): string {
		return 'Not authorized. On localhost: sign out and sign in again, and run Firebase emulators (auth + functions).';
	}

	/** Server: recompute `cycleName` on schedules after cycle edits (replaces client `updateSchedulesCycle`). */
	async repairScheduleCycleNames(payload: {
		referenceDate: string;
		afterEndDateTime: string;
		source?: string;
	}): Promise<{ ok: boolean; updatedCount: number; source: string }> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.repairScheduleCycleNames',
			() =>
				lastValueFrom(
					this.http.post<{ ok: boolean; updatedCount: number; source: string }>(
						`${this.getFunctionsEndpoint()}/schedules/repairCycleNames`,
						payload,
						{ headers },
					),
				),
		);
	}

	/** Server: reopen closed MAK schedules (`eventType: closed` → `default`; sandbox utility). */
	async reopenClosedMAKSchedules(payload: {
		limit?: number;
		source?: string;
	}): Promise<{ ok: boolean; reopenedCount: number; scheduleIds: string[]; source: string }> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.reopenClosedMAKSchedules',
			() =>
				lastValueFrom(
					this.http.post<{ ok: boolean; reopenedCount: number; scheduleIds: string[]; source: string }>(
						`${this.getFunctionsEndpoint()}/schedules/reopenClosedMAK`,
						payload,
						{ headers },
					),
				),
			{ limit: payload.limit ?? 25 },
		);
	}

	/** Server proxy: list HighLevel contacts (token not exposed to the browser). */
	async listHighLevelContacts(): Promise<HighLevelContact[]> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		const result = await this.invokeHttp(
			'app:Firebase_Functions_Service.listHighLevelContacts',
			() =>
				lastValueFrom(
					this.http.get<{ contacts: HighLevelContact[]; count: number }>(
						`${this.getFunctionsEndpoint()}${HIGHLEVEL_GAPI_PATHS.listContacts}`,
						{ headers },
					),
				),
		);
		return result.contacts ?? [];
	}

	/** Server: create-only push of inactive parent MAS accounts into HighLevel (win-back list). */
	async createHighLevelInactiveParentContacts(payload: {
		source?: string;
		dryRun?: boolean;
		limit?: number;
	}): Promise<HighLevelInactiveParentsCreateResult> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.createHighLevelInactiveParentContacts',
			() =>
				lastValueFrom(
					this.http.post<HighLevelInactiveParentsCreateResult>(
						`${this.getFunctionsEndpoint()}${HIGHLEVEL_GAPI_PATHS.createInactiveParents}`,
						payload,
						{ headers },
					),
				),
		);
	}

	/** Server: sync active parents — create/promote HighLevel contacts and set mas.highLevelId. */
	async syncActiveParentContacts(payload: {
		source?: string;
		dryRun?: boolean;
		limit?: number;
	}): Promise<SyncActiveParentContactsResult> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.syncActiveParentContacts',
			() =>
				lastValueFrom(
					this.http.post<SyncActiveParentContactsResult>(
						`${this.getFunctionsEndpoint()}${HIGHLEVEL_GAPI_PATHS.syncActiveParents}`,
						payload,
						{ headers },
					),
				),
		);
	}

	/** Server: set mas.highLevelId when email or phone matches a HighLevel contact. */
	async linkMasHighLevelIds(payload: {
		source?: string;
		dryRun?: boolean;
		limit?: number;
	}): Promise<LinkMasHighLevelIdsResult> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp(
			'app:Firebase_Functions_Service.linkMasHighLevelIds',
			() =>
				lastValueFrom(
					this.http.post<LinkMasHighLevelIdsResult>(
						`${this.getFunctionsEndpoint()}${HIGHLEVEL_GAPI_PATHS.linkMasIds}`,
						payload,
						{ headers },
					),
				),
		);
	}

	/*************************************************************************
	 * Utilities API
	 *************************************************************************/

	getEnviroment(): Observable<any> {
		return this.http.get(`${this.getFunctionsEndpoint()}/currentEnvironment`);
	}

	async getFirecloudDocuments(): Promise<unknown> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		return this.invokeHttp('app:Firebase_Functions_Service.getFirecloudDocuments', () =>
			lastValueFrom(this.http.get(`${this.getFunctionsEndpoint()}/sandbox/documents`, { headers })),
		);
	}

	async runViolationsJob(options: { dryRun: boolean }): Promise<ViolationsJobResult> {
		const headers = await this.buildAuthHeaders();
		if (!headers) throw new Error('Invalid token');

		const dryRun = options.dryRun;
		const url = `${this.getSandboxEndpoint()}/violationsJob`;

		return this.invokeHttp('app:Firebase_Functions_Service.runViolationsJob', () =>
			lastValueFrom(
				this.http.post<ViolationsJobResult>(
					url,
					{ dryRun },
					{
						headers,
						params: { dryRun: String(dryRun) },
					},
				),
			),
			{ dryRun, url },
		);
	}

	sendCancelationEmail(list: string[], schedule: Schedule) {
		if (list.length > 0) {
			const message: EmailMessage = {
				to: 'accounts@yongsa.net',
				bcc: list,
				message: {
					subject: `${schedule.summary} on ${schedule.start.dateTime} was removed from the schedule`,
					text: `Your account had a reservation for this date. We removed it from the schedule.`,
				},
			};

			const sendEmail = new SendEmail(this.firestore);
			sendEmail.writeEmailToFirestore(message);
		}
	}
}
